import { useEffect } from "react"
import { Link } from "react-router-dom"
import { useQuotaGate, formatResetIST, sessionPath, planStateOf, synthesizeOffer, type Offer, type QuotaDenial } from "@/lib/quotaGate"
import { dismiss, paywallKey } from "@/lib/paywallSession"
import { featureLabel } from "@/features/nira2/matter/features"
import { Overlay } from "@/features/nira2/answer/Overlay"
import { useMeStore } from "@/features/auth/hooks/useCurrentUser"
import { useQuotaRefresh } from "@/features/billing/hooks/useEntitlements"
import { useCheckout } from "@/features/billing/hooks/useCheckout"
import { PRO_FALLBACK_LINES, allowanceLines, metricLabel, plusLines } from "@/features/billing/copy"
import { FALLBACK_ADDON_MINOR, addonFor, planOf, priceCopy, priceMinor, rupees, useAddons, usePlans } from "@/features/billing/plan"
import { megabytesLabel } from "@/lib/uploadLimits"
import { track } from "@/lib/analytics"
import "@/features/nira2/answer/pro.css"
import "@/features/nira2/ds/toast.css"
import "./paywall.css"

const UPLOAD_METRIC = "attachment.upload"
const FALLBACK_FILE_BYTES = 50 * 1024 * 1024

/** The offer to draw: the server's, or the doc's rules applied to what the 402 says (an older server sends none). */
/** " a day", " a month", " per draft"; `fallback` for lifetime or an unknown window. */
function perWindow(window: string | null, fallback: string): string {
  return window === "day" ? " a day" : window === "month" ? " a month" : window === "draft" ? " per draft" : fallback
}

function offerOf(d: QuotaDenial): Offer {
  if (d.offer) return d.offer
  const state = d.planState ?? (d.subscribed ? "pro" : "free")
  return synthesizeOffer(state, d.metric, d.window, d.limit).offer
}

/** One modal for every 402 and every Upgrade button, drawn by offer: Plus or Pro, Pro, Pro with Plus credited, an add-on, or the reset time. Mounted once per shell. Rendered as the Pro overlay. */
export function PaywallModal() {
  const { denial, close } = useQuotaGate()
  const me = useMeStore((s) => s.me)
  const open = !!denial && denial.code === "quota_exhausted"
  const plans = usePlans(open)
  const addons = useAddons(open)
  const checkout = useCheckout()
  useQuotaRefresh()

  const { setError } = checkout
  useEffect(() => {
    if (!denial) return
    setError("")
    track("paywall_shown", {
      metric: denial.metric, reason: denial.upgrade ? "upgrade" : denial.code, feature: denial.feature,
      offer: denial.offer, trigger: denial.trigger,
    })
  }, [denial, setError])

  if (!denial) return null
  const { busy, error } = checkout
  const reset = formatResetIST(denial.resetsAt)
  const label = metricLabel(denial.metric)
  // The server names the feature and says the sentence; the copy below is the fallback when it does not.
  const feature = featureLabel(denial.feature)
  const message = denial.message
  const isUpload = denial.metric === UPLOAD_METRIC
  const offer = offerOf(denial)
  const plusPlan = planOf(plans, "plus")
  const proPlan = planOf(plans, "pro")

  const notNow = () => {
    if (!denial.upgrade) dismiss(paywallKey(denial))
    close()
  }
  const choose = async (plan: "plus" | "pro") => {
    track("subscribe_clicked", { from: "paywall", plan, offer, trigger: denial.trigger })
    if ((await checkout.subscribe(plan)) === "active") close()
  }
  const buy = async (sku: string) => {
    track("addon_clicked", { sku, trigger: denial.trigger })
    if ((await checkout.buy(sku, denial.scopeKey)) === "paid") close()
  }
  const startLabel = (plan: "plus" | "pro", text: string) => (busy === plan ? "Opening checkout…" : text)
  const proLines = (): string[] => {
    const l = allowanceLines(me?.entitlements ?? null, proPlan).filter(Boolean)
    return proPlan?.allowances.length ? l : PRO_FALLBACK_LINES
  }
  const errorLine = error ? <p className="pwl-err" role="alert">{error}</p> : null
  const notNowBtn = <button type="button" className="nr-ov-not" onClick={notNow}>Not now</button>

  let eyebrow: string | null = null
  let title: string
  let body: JSX.Element
  if (denial.code === "draft_in_progress") {
    title = "A draft is already open"
    body = (
      <>
        <p className="nr-ov-p">{message ?? `Finish or close your open ${label} before starting another.`}</p>
        <div className="pwl-act">
          {denial.sessionId && (
            <Link className="nr-btn" data-variant="primary" to={sessionPath(denial.metric, denial.sessionId)} onClick={close}>
              Open the session
            </Link>
          )}
          <button type="button" className="nr-ov-not" onClick={close}>Not now</button>
        </div>
      </>
    )
  } else if (offer === "none") {
    // Nothing to sell: say what is used and when it comes back.
    const monthly = denial.window === "month"
    eyebrow = monthly ? "Monthly limit" : "Daily limit"
    title = feature ?? (monthly ? "Monthly limit reached" : "Daily limit reached")
    const used = denial.used ?? denial.limit
    const windowWord = monthly ? "this month" : "today"
    body = (
      <>
        <p className="nr-ov-p">{message ?? `You have used ${used} of ${denial.limit} ${label} for ${windowWord}.`}</p>
        {denial.limit !== null && <p className="pwl-stat">{used} of {denial.limit} used {windowWord}</p>}
        <p className="nr-ov-foot">
          {monthly
            ? (reset ? `Your allowance resets on the 1st, at ${reset}.` : "Your allowance resets on the 1st of the month.")
            : (reset ? `Your allowance resets at ${reset}.` : "Your allowance resets at midnight IST.")}
        </p>
        <div className="pwl-act">
          <button type="button" className="nr-btn" onClick={close}>Close</button>
        </div>
      </>
    )
  } else if (offer === "addon_draft" || offer === "addon_edits") {
    const metric = offer === "addon_draft" ? "order.draft" : "order.draft_edit"
    const addon = addonFor(addons, metric, denial.addon?.sku)
    const sku = denial.addon?.sku ?? addon?.sku ?? (offer === "addon_draft" ? "extra_draft" : "extra_edits")
    const price = denial.addon?.priceMinor ?? addon?.price_minor ?? FALLBACK_ADDON_MINOR[sku] ?? 0
    const qty = denial.addon?.quantity ?? addon?.quantity ?? (offer === "addon_draft" ? 1 : 5)
    eyebrow = "Nira Pro"
    title = offer === "addon_draft" ? "Another order draft" : "More edits"
    body = (
      <>
        <p className="nr-ov-p">{message ?? (offer === "addon_draft" ? "This month's draft is used." : "This draft's edits are used.")}</p>
        <p className="nr-ov-p">
          {offer === "addon_draft"
            ? "Add one more order draft for this month."
            : `Add ${qty} more edits to this draft.`}
        </p>
        <p className="pwl-price">{rupees(price)}, once</p>
        {errorLine}
        <div className="pwl-act">
          <button type="button" className="nr-btn" data-variant="primary" disabled={!!busy} aria-busy={busy === sku} onClick={() => { void buy(sku) }}>
            {busy === sku ? "Opening checkout…" : `Buy for ${rupees(price)}`}
          </button>
          {notNowBtn}
        </div>
      </>
    )
  } else {
    const fileMb = megabytesLabel(proPlan?.max_file_bytes ?? (planStateOf(me?.entitlements ?? null) === "pro" ? me?.max_file_bytes : null) ?? FALLBACK_FILE_BYTES)
    const proUploads = proPlan?.allowances.find((a) => a.metric === UPLOAD_METRIC)?.quantity ?? 100
    const credited = offer === "pro_credit"
    const plusMinor = priceMinor(plusPlan, "plus")
    const proMinor = priceMinor(proPlan, "pro")
    eyebrow = offer === "plus_or_pro" ? "Nira Plus or Pro" : "Nira Pro"
    title = denial.upgrade
      ? (offer === "plus_or_pro" ? "Nira Plus or Pro" : credited ? "Move to Nira Pro" : "Nira Pro")
      : (isUpload ? "Uploads" : (feature ?? (offer === "plus_or_pro" ? "Choose a plan" : credited ? "Move to Nira Pro" : "Subscribe to continue")))
    const sentence = denial.upgrade
      ? (offer === "plus_or_pro" ? "More questions, or the full work product:" : "Everything Nira does, more of it each day:")
      : isUpload
        ? `Free plan: ${denial.limit ?? 10} uploads. Pro: ${proUploads} a day, up to ${fileMb} each.`
        : (message ?? `You have used your free ${label}. Nira Pro gives you more each day.`)

    let choices: JSX.Element
    if (offer === "plus_or_pro") {
      choices = (
        <div className="pwl-plans">
          <section className="pwl-plan" aria-label="Nira Plus">
            <h3>Nira Plus</h3>
            <p className="pwl-amt">{rupees(plusMinor)}<small>/month</small></p>
            <ul className="pwl-list">{plusLines(plusPlan).map((l) => <li key={l}>{l}</li>)}</ul>
            <button type="button" className="nr-btn" disabled={!!busy} aria-busy={busy === "plus"} onClick={() => { void choose("plus") }}>
              {startLabel("plus", "Start Plus")}
            </button>
          </section>
          <section className="pwl-plan pro" aria-label="Nira Pro">
            <h3>Nira Pro</h3>
            <p className="pwl-amt">{rupees(proMinor)}<small>/month</small></p>
            <ul className="pwl-list">{proLines().map((l) => <li key={l}>{l}</li>)}</ul>
            <button type="button" className="nr-btn" data-variant="primary" disabled={!!busy} aria-busy={busy === "pro"} onClick={() => { void choose("pro") }}>
              {startLabel("pro", "Start Pro")}
            </button>
          </section>
        </div>
      )
    } else {
      const proRow = proPlan?.allowances.find((a) => a.metric === denial.metric)
      const freeCell = denial.limit !== null ? String(denial.limit) : "First one"
      choices = (
        <>
          {offer === "pro" && !denial.upgrade && !isUpload && (
            <dl className="pwl-cmp" aria-label="Free and Pro allowance">
              <div className="pwl-col">
                <dt>Free</dt>
                <dd>{freeCell}<small>{denial.limit !== null ? `${label}${perWindow(denial.window, " in all")}` : label}</small></dd>
              </div>
              <div className="pwl-col pro">
                <dt>Nira Pro</dt>
                <dd>{proRow ? (proRow.quantity === null ? "Any number of" : String(proRow.quantity)) : "More"}<small>{proRow ? `${label}${perWindow(proRow.window, "")}` : "every day"}</small></dd>
              </div>
            </dl>
          )}
          {credited && <p className="pwl-credit">This month's {rupees(plusMinor)} is credited.</p>}
          <ul className="pwl-list">{proLines().map((l) => <li key={l}>{l}</li>)}</ul>
          <p className="pwl-price">
            {credited ? `Pay ${rupees(Math.max(proMinor - plusMinor, 0))} now. Pro starts at once.` : priceCopy(proPlan, "pro")}
          </p>
          {errorLine}
          <div className="pwl-act">
            {denial.canSubscribe ? (
              <button type="button" className="nr-btn" data-variant="primary" disabled={!!busy} aria-busy={busy === "pro"} onClick={() => { void choose("pro") }}>
                {startLabel("pro", credited ? "Upgrade to Pro" : "Start Pro")}
              </button>
            ) : (
              <p className="nr-ov-p">Subscriptions are not available right now.</p>
            )}
            {notNowBtn}
          </div>
        </>
      )
    }
    body = (
      <>
        <p className="nr-ov-p">{sentence}</p>
        {choices}
        {offer === "plus_or_pro" && (
          <>
            {errorLine}
            <div className="pwl-act">{notNowBtn}</div>
          </>
        )}
      </>
    )
  }

  return (
    <Overlay label={title} onClose={close}>
      {eyebrow && <p className="nr-ov-k">{eyebrow}</p>}
      <h2 className="nr-ov-h">{title}</h2>
      {body}
    </Overlay>
  )
}
